import { ContestState } from '@maxpreps/apiserviceclients/apiModels';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import { pluralize } from '@maxpreps/shared/functions/primitiveHelpers/stringHelpers';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import { useIsMobile } from '@maxpreps/shared/hooks/useIsScreenSize';
import Table from '@maxpreps/shared/ui/Table/Table';
import { StyledTablePlaceholderButton } from '@maxpreps/shared/ui/Table/TablePlaceholder';
import dynamic from 'next/dynamic';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import fetchFeaturedGame from '../../data/schedule/featured-game';
import useScheduleKeyboardShortcuts from '../../utils/schedule/useScheduleKeyboardShortcuts';
import getIsPublishedSportSeason from './../../utils/getIsPublishedSportSeason';
import useScheduleContext from './ScheduleContext';
import ScheduleRow from './ScheduleRow/ScheduleRow';
import { getTableConfig } from './ScheduleTableConfig';

const FeaturedGame = dynamic(() => import('./FeaturedGame/FeaturedGame'), {
  ssr: false
});

const isValidFeaturedGame = (contest, featuredGameData) => {
  if (!featuredGameData || contest.isDeleted) return false;
  const { contestId: featuredId, contestState: featuredState } =
    featuredGameData.contest;
  const { contestId: currentId, contestState: currentState } = contest;
  if (currentId !== featuredId) return false;
  // It is possible for game state to be temporarily out-of-sync because of caching
  // Do not display until data is synced
  if (currentState !== featuredState) {
    // console.log('FG_STATE', 'cur:', currentState, 'feat:', featuredState);
    return false;
  }
  // console.log('FG_STATE', 'both:', currentState);
  switch (currentState) {
    case ContestState.Pregame:
    case ContestState.ContestInProgress:
    case ContestState.ScoreNotReported:
    case ContestState.Boxscore:
      return true;
    default:
      return false;
  }
};

const ScheduleTable = () => {
  const {
    contests,
    showDeleted,
    setShowDeleted,
    copySchedule,
    otherLevelTeams
  } = useScheduleContext();

  useScheduleKeyboardShortcuts(showDeleted, setShowDeleted);

  const teamContext = useTeamContext();
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const [featuredGameData, setFeaturedGameData] = useState(null);

  const {
    data: teamData,
    sportData,
    stateData,
    teamSeasonPickerData
  } = teamContext;
  const { teamId, sportSeasonId } = teamData;

  const isPublishedSportSeason = getIsPublishedSportSeason(
    sportSeasonId,
    teamSeasonPickerData
  );

  useEffect(() => {
    (async () => {
      const response = await fetchFeaturedGame(teamId, sportSeasonId);
      setFeaturedGameData(response as any);
    })();
  }, [teamId, sportSeasonId]);

  const scheduleTableRows = useMemo(() => {
    const rows: ReactNode[] = [];

    for (let i = 0; i < contests?.length; i++) {
      const contest = contests[i];
      if (showDeleted || !contest.isDeleted) {
        if (
          isPublishedSportSeason &&
          !isAdmin &&
          isValidFeaturedGame(contest, featuredGameData)
        ) {
          rows.push(
            <tr key="featured-game">
              {/* @ts-ignore */}
              <td colSpan={5} css="padding-right: 0;">
                <FeaturedGame
                  featuredGameData={featuredGameData}
                  sportData={sportData}
                  stateData={stateData}
                />
              </td>
            </tr>
          );
        } else {
          rows.push(<ScheduleRow key={contest.contestId} contest={contest} />);
        }
      }
    }
    return rows;
  }, [
    contests,
    showDeleted,
    featuredGameData,
    isAdmin,
    sportData,
    stateData,
    isPublishedSportSeason
  ]);

  const tableConfig = getTableConfig(isAdmin, isMobile);

  const adminCopyButton =
    isAdmin && otherLevelTeams.length ? (
      <StyledTablePlaceholderButton onClick={copySchedule}>
        Copy Schedule
      </StyledTablePlaceholderButton>
    ) : undefined;

  const placeholderData = isAdmin
    ? {
        header: 'Add Your Schedule',
        subheader:
          otherLevelTeams.length > 0
            ? 'Click on the add button above or save time by copying the schedule from another team.'
            : 'Click on the add button above to start building your schedule.',
        action: adminCopyButton
      }
    : {
        header: `No ${pluralize(sportData.contestName)}`,
        subheader: `The coach has not entered any ${pluralize(
          sportData.contestName.toLowerCase()
        )}.`
      };

  return (
    <Table
      config={tableConfig}
      placeholderData={placeholderData}
      rows={scheduleTableRows}
    />
  );
};

export default ScheduleTable;
